import {
  replyMatches,
  type InboxItem,
  type PertoTask,
  type PertoTaskPlace,
} from '@secretario/shared';
import { deviceContactNames } from './native.js';

/**
 * Reconhecer a resposta de um negócio do pedido (PA2) pelo aviso do WhatsApp. O WhatsApp
 * mostra o nome salvo na agenda (e não o número) quando o negócio é um contato, e o código
 * interno da conversa não traz o telefone. Então: o número ou o nome do negócio; o nome salvo
 * na agenda para aquele número (se a pessoa deixou ver os contatos); e, sem nada disso, uma
 * mensagem com cara de resposta vira pergunta na página do pedido ("é a resposta de quem?").
 * Tudo fica no aparelho.
 */

const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

let names: Map<string, string[]> | null = null;
let namesAt = 0;

/** Os nomes da agenda (só com a permissão já dada); relê a cada 10 minutos ou quando pedem. */
export async function loadContactNames(force = false): Promise<void> {
  if (!force && namesAt && Date.now() - namesAt < 10 * 60_000) return;
  names = await deviceContactNames();
  namesAt = Date.now();
}

/** Quem escreveu é este negócio: pelo número, pelo nome dele ou pelo nome salvo na agenda. */
export function placeMatches(from: string, place: Pick<PertoTaskPlace, 'name' | 'phone'>): boolean {
  if (replyMatches(from, place)) return true;
  const digits = (place.phone ?? '').replace(/\D/g, '').slice(-8);
  if (digits.length < 8 || !names) return false;
  const who = fold(from.replace(/^~/, ''));
  return who.length >= 2 && (names.get(digits) ?? []).some((name) => fold(name) === who);
}

/**
 * É o próprio pedido (ou o lembrete) que ela mandou, visto de volta: no aparelho com o
 * WhatsApp Business do negócio, ou repassado. Nunca é resposta.
 */
export function isOwnAsk(text: string, task: PertoTask): boolean {
  const said = fold(text);
  return task.places.some((place) => {
    const sent = fold(place.text).slice(0, 60);
    return sent.length >= 12 && said.includes(sent);
  });
}

/** Tem cara de resposta a um pedido de horário e valor. */
export function looksLikeReply(text: string): boolean {
  return /(\b\d{1,2}\s?h\b|\b\d{1,2}:\d{2}\b|r\$\s?\d|\bvalor|\bpre[cç]o|\bhor[aá]rio|\bdispon[ií]vel|\bamanh[aã]|\bhoje\b|\bsegunda|\bter[cç]a|\bquarta|\bquinta|\bsexta|\bs[aá]bado|\bdomingo|\bconsigo|\bpodemos|\bposso\b|\bagenda|\bor[cç]amento|\bvisita)/i.test(
    text,
  );
}

export type ReplyCandidate = {
  key: string;
  taskId: string;
  from: string;
  text: string;
  at: number;
};

const CANDIDATES_KEY = 'secretario.perto.talvez';
const NOT_KEY = 'secretario.perto.nao-e';
export const PERTO_CANDIDATES_EVENT = 'secretario:perto-talvez';
/** Pedido para olhar a barra de novo (a pessoa acabou de liberar os contatos). */
export const PERTO_RECHECK_EVENT = 'secretario:perto-reler';
const KEEP_MS = 48 * 60 * 60_000;

export function replyCandidates(taskId?: string): ReplyCandidate[] {
  try {
    const all = JSON.parse(localStorage.getItem(CANDIDATES_KEY) ?? '[]') as ReplyCandidate[];
    const fresh = all.filter((one) => Date.now() - one.at < KEEP_MS);
    return taskId ? fresh.filter((one) => one.taskId === taskId) : fresh;
  } catch {
    return [];
  }
}

function saveCandidates(list: ReplyCandidate[]): void {
  try {
    localStorage.setItem(CANDIDATES_KEY, JSON.stringify(list.slice(-12)));
  } catch {
    /* sem memória: a pergunta não aparece, e a pessoa ainda pode colar a resposta */
  }
  window.dispatchEvent(new CustomEvent(PERTO_CANDIDATES_EVENT));
}

export function addReplyCandidate(candidate: ReplyCandidate): void {
  if (settled(candidate.taskId, candidate.from)) return;
  // A mesma conversa atualiza o aviso a cada fala: fica só a última (as falas vêm juntas).
  const list = replyCandidates().filter(
    (one) => !(one.taskId === candidate.taskId && one.from === candidate.from),
  );
  saveCandidates([...list, candidate]);
}

/** Respondida ou "não é": sai de todos os pedidos (a mesma conversa não pergunta de novo). */
export function dropReplyCandidate(candidate: ReplyCandidate): void {
  saveCandidates(replyCandidates().filter((one) => one.from !== candidate.from));
  try {
    const no = new Set(JSON.parse(localStorage.getItem(NOT_KEY) ?? '[]') as string[]);
    no.add(`${candidate.taskId}|${candidate.from}`);
    localStorage.setItem(NOT_KEY, JSON.stringify([...no].slice(-50)));
  } catch {
    /* sem memória */
  }
}

/** Ela já disse que esta conversa não é deste pedido (ou já respondeu por ela). */
function settled(taskId: string, from: string): boolean {
  try {
    return (JSON.parse(localStorage.getItem(NOT_KEY) ?? '[]') as string[]).includes(
      `${taskId}|${from}`,
    );
  } catch {
    return false;
  }
}

/** Os negócios do pedido que ainda esperam resposta e já tinham recebido o pedido antes do aviso. */
export function waitingPlaces(task: PertoTask, at: number): PertoTaskPlace[] {
  return task.places.filter(
    (place) =>
      place.state === 'pedido' &&
      !place.reply &&
      place.askedAt !== null &&
      at >= new Date(place.askedAt).getTime(),
  );
}

/** O texto do aviso: as falas recebidas desde a última resposta, ou o texto do próprio aviso. */
export const inboxText = (item: InboxItem) =>
  (item.lines?.map((line) => line.text).join('\n') || item.text).slice(0, 2000);

let open: PertoTask[] = [];
/** Os pedidos abertos agora (o vigia do Perto de Mim atualiza). */
export function setOpenPertoTasks(tasks: PertoTask[]): void {
  open = tasks;
}

/**
 * Este aviso é (ou pode ser) a resposta de um pedido aberto: quem cuida é a página do pedido,
 * e ele não entra também na fila de "mensagens de outros apps". Se ela disser "não é", volta.
 */
export function pertoClaims(item: InboxItem): boolean {
  if (item.mine || item.from.includes(' · ')) return false;
  return open.some((task) => {
    const waiting = waitingPlaces(task, item.at);
    if (waiting.length === 0) return false;
    const text = inboxText(item);
    if (isOwnAsk(text, task)) return false;
    if (waiting.some((place) => placeMatches(item.from, place))) return true;
    return !settled(task.id, item.from) && looksLikeReply(text);
  });
}
